import React, { useState, useEffect } from 'react'
import { ServerConfig } from 'config'
import axios from 'axios'
import { useSelector } from 'react-redux'
import {
  Typography, Grid, Button, Checkbox, FormControlLabel,
  Select, MenuItem, FormControl, InputLabel, Switch
} from '@material-ui/core'
import loading_gif from '../../assets/img/loading.gif'

const OPERATION_TYPES = [
  { id: 0, name: 'Recycling Center (RC)' },
  { id: 1, name: 'Reverse Vending Machine (RVM)' },
  { id: 2, name: 'Curbside Program (CS)' },
  { id: 3, name: 'Drop-off Collection Program (CP)' },
  { id: 4, name: 'Community Service Program (SP)' },
  { id: 5, name: 'Bagdrop' },
]

const FeePolicies = () => {
  const token = useSelector(state => state.auth.access_token)

  const [policies, setPolicies] = useState([])
  const [sites, setSites] = useState([])
  const [machines, setMachines] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const [scopeType, setScopeType] = useState('tenant')
  const [selectedSiteId, setSelectedSiteId] = useState('')
  const [selectedBeltId, setSelectedBeltId] = useState('')
  const [selectedOpType, setSelectedOpType] = useState('')
  const [coversDigitalFees, setCoversDigitalFees] = useState(false)

  const authConfig = () => ({
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })

  async function fetchPolicies() {
    setIsLoading(true)
    setError(null)
    try {
      const res = await axios.get(`${ServerConfig.SERVER_URL}/tenant/fee_policies`, authConfig())
      setPolicies(res.data)
    } catch (err) {
      setError('Failed to load policies.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPolicies()
    axios.get(`${ServerConfig.SERVER_URL}/tenant/tenant_sites`, authConfig())
      .then(res => setSites(res.data))
    axios.get(`${ServerConfig.SERVER_URL}/tenant/machines`, authConfig())
      .then(res => setMachines(res.data))
  }, [token])

  async function createPolicy() {
    setError(null)
    const body = { covers_digital_fees: coversDigitalFees }

    if (scopeType === 'belt') {
      body.belt_id = selectedBeltId
    } else if (scopeType === 'site') {
      body.site_id = selectedSiteId
    }

    if (scopeType !== 'belt' && selectedOpType !== '') {
      body.operationtype_id = parseInt(selectedOpType)
    }

    try {
      await axios.post(`${ServerConfig.SERVER_URL}/tenant/fee_policy`, body, authConfig())
      setScopeType('tenant')
      setSelectedSiteId('')
      setSelectedBeltId('')
      setSelectedOpType('')
      setCoversDigitalFees(false)
      fetchPolicies()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create policy.')
    }
  }

  async function togglePolicy(policy) {
    try {
      await axios.patch(
        `${ServerConfig.SERVER_URL}/tenant/fee_policy/${policy.id}`,
        { covers_digital_fees: !policy.covers_digital_fees },
        authConfig()
      )
      fetchPolicies()
    } catch (err) {
      setError('Failed to update policy.')
    }
  }

  async function deletePolicy(policy) {
    try {
      await axios.delete(`${ServerConfig.SERVER_URL}/tenant/fee_policy/${policy.id}`, authConfig())
      fetchPolicies()
    } catch (err) {
      setError('Failed to delete policy.')
    }
  }

  function scopeLabel(policy) {
    if (policy.belt_id) {
      const machine = machines.find(m => m.id === policy.belt_id)
      return `Belt: ${machine ? machine.name || machine.id : policy.belt_id}`
    }
    const opType = policy.operationtype_id !== null && policy.operationtype_id !== undefined
      ? OPERATION_TYPES.find(o => o.id === policy.operationtype_id)?.name ?? policy.operationtype_id
      : null
    if (policy.site_id) {
      const site = sites.find(s => s.id === policy.site_id)
      const siteName = site ? site.name : policy.site_id
      return opType ? `Site: ${siteName} / ${opType}` : `Site: ${siteName}`
    }
    return opType ? `Tenant / ${opType}` : 'Tenant (all)'
  }

  const canAdd = scopeType === 'tenant'
    || (scopeType === 'site' && selectedSiteId !== '')
    || (scopeType === 'belt' && selectedBeltId !== '')

  return (
    <>
      {isLoading && <img src={loading_gif} style={{ width: '150px' }} />}

      {error && (
        <Typography style={{ color: 'red', marginBottom: '10px' }}>{error}</Typography>
      )}

      {!isLoading && (
        <>
          <table className="table table-striped" style={{ width: '100%', marginBottom: '30px' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px' }}>Scope</th>
                <th style={{ padding: '10px' }}>Covers Digital Fees</th>
                <th style={{ padding: '10px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {policies.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ padding: '10px', textAlign: 'center' }}>
                    No policies configured. Fees are charged to the customer by default.
                  </td>
                </tr>
              )}
              {policies.map(policy => (
                <tr key={policy.id}>
                  <td style={{ padding: '10px' }}>{scopeLabel(policy)}</td>
                  <td style={{ padding: '10px' }}>
                    <Switch
                      checked={policy.covers_digital_fees}
                      onChange={() => togglePolicy(policy)}
                    />
                    {policy.covers_digital_fees ? 'Tenant covers' : 'Customer pays'}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <Button size="small" onClick={() => deletePolicy(policy)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Typography variant="h3" style={{ marginBottom: '15px' }}>Add Policy</Typography>

          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select
                  value={scopeType}
                  onChange={e => {
                    setScopeType(e.target.value)
                    setSelectedSiteId('')
                    setSelectedBeltId('')
                    setSelectedOpType('')
                  }}
                >
                  <MenuItem value="tenant">Tenant-wide</MenuItem>
                  <MenuItem value="site">Site</MenuItem>
                  <MenuItem value="belt">Belt</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {scopeType === 'site' && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Site</InputLabel>
                  <Select value={selectedSiteId} onChange={e => setSelectedSiteId(e.target.value)}>
                    {sites.map(site => (
                      <MenuItem key={site.id} value={site.id}>{site.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {scopeType === 'belt' && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Belt</InputLabel>
                  <Select value={selectedBeltId} onChange={e => setSelectedBeltId(e.target.value)}>
                    {machines.map(machine => (
                      <MenuItem key={machine.id} value={machine.id}>{machine.name || machine.id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {scopeType !== 'belt' && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Operation Type (optional)</InputLabel>
                  <Select value={selectedOpType} onChange={e => setSelectedOpType(e.target.value)}>
                    <MenuItem value="">Any</MenuItem>
                    {OPERATION_TYPES.map(op => (
                      <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={coversDigitalFees}
                    onChange={e => setCoversDigitalFees(e.target.checked)}
                  />
                }
                label="Covers Digital Fees"
              />
            </Grid>

            <Grid item xs={12} sm={1}>
              <Button onClick={createPolicy} disabled={!canAdd}>Add</Button>
            </Grid>
          </Grid>
        </>
      )}
    </>
  )
}

export default FeePolicies
